import React, { useState, useEffect, Suspense } from "react";
import { Pressable, View, Text, ActivityIndicator } from "react-native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Feather from "@expo/vector-icons/Feather";

import MainTabNavigator from "@/navigation/MainTabNavigator";
import type { MainTabParamList } from "@/navigation/MainTabNavigator";
import type { NavigatorScreenParams } from "@react-navigation/native";
import { useScreenOptions } from "@/hooks/useScreenOptions";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { isOAuthFlow } from "@/lib/oauthCapture";
import { isExtensionConnectFlow } from "@/lib/extensionConnectCapture";
import { isNativeHandoffFlow } from "@/lib/nativeHandoffCapture";

import { hasPendingWatchIntent } from "@/lib/watchIntent";
import {
  hasCompletedOnboarding,
  LEGACY_ONBOARDING_STORAGE_KEY,
  ONBOARDING_STORAGE_KEY,
  shouldShowOnboarding,
} from "@/lib/onboardingState";

// import { SignalMetadataDemoScreen } from "@/screens/SignalMetadataDemoScreen"; // QA only — uncomment for visual review

export type RootStackParamList = {
  Auth: undefined;
  Onboarding: undefined;
  Main: NavigatorScreenParams<MainTabParamList> | undefined;
  AlertDetail: { alertId: string };
  Pricing: undefined;
  Intelligence: undefined;
  Portfolio: undefined;
  MarketHeatmap: undefined;
  DarkPool: undefined;
  FlashCrash: undefined;
  SymbolSearch: undefined;
  Symbol: { symbol: string; entryEvidence?: { id: string; symbol: string; line: string; at: string; source: string; scope?: string; windowMinutes?: number } };
  Insight: undefined;
  Accounts: undefined;
  ContagionMap: { focusSymbol?: string; peerSymbol?: string };
  CapitalFlow: { symbol?: string };
  ForecastPanel: { symbol?: string; alertId?: string | number };
  AlphaAttribution: undefined;
  CaseStudies: undefined;
  OcaTrace: { symbol?: string };
  TemporalArbitrage: { symbol?: string };
  IvRvTermStructure: { symbol?: string };
  WatchlistV2: undefined;
  WatchSetup: undefined;
  AlertsV2: undefined;
  EngineExplorer: undefined;
  RiskMonitor: undefined;
  EngineDeepDive: { engineType: string; symbol?: string };
  SentimentTimeline: undefined;
  VolatilitySurface: undefined;
  Analytics: undefined;
  MonitoringRules: {
    // Optional pre-fill values passed from AlertDetail's context-aware Defense
    // Rule CTA. When present, the MonitoringRules screen lands the user on a
    // rule form already shaped for the originating alert.
    prefillSymbol?: string;
    prefillRuleType?: string;
    prefillValues?: Record<string, string | number>;
    prefillLabel?: string;
  } | undefined;
  Learn: undefined;
  Developer: undefined;
  ConnectHub: undefined;
  ExtensionConnect: undefined;
  PortfolioCommand: undefined;
  DeepDive: undefined;
  SituationWatch: undefined;
  PortfolioYourMoney: undefined;
  AttentionRadar: undefined;
  EvidenceGraph: undefined;
  SymbolIntel: { symbol?: string } | undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

// Keep account, developer, and integration code out of Today’s initial graph.
// These screens carry wallet/SDK dependencies and are only needed after a
// reader explicitly opens the corresponding route.
function deferredScreen(loader: () => Promise<{ default: React.ComponentType<any> }>) {
  const Lazy = React.lazy(loader);
  return function DeferredScreen(props: any) {
    return <Suspense fallback={<View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}><ActivityIndicator accessibilityLabel="Loading page" /></View>}><Lazy {...props} /></Suspense>;
  };
}
const AccountsScreen = deferredScreen(() => import("@/screens/AccountsScreen"));
const AuthScreen = deferredScreen(() => import("@/screens/AuthScreen"));
const OnboardingScreen = deferredScreen(() => import("@/screens/OnboardingScreenV2"));
const DeveloperScreen = deferredScreen(() => import("@/screens/DeveloperScreen"));
const ConnectHubScreen = deferredScreen(() => import("@/screens/ConnectHubScreen"));
const PortfolioCommandScreen = deferredScreen(() => import("@/screens/PortfolioCommandScreen"));
const AlertDetailScreen = deferredScreen(() => import("@/screens/AlertDetailScreen"));
const PricingScreen = deferredScreen(() => import("@/screens/PricingScreen"));
const MarketHeatmapScreen = deferredScreen(() => import("@/screens/MarketHeatmapScreen"));
const DarkPoolScreen = deferredScreen(() => import("@/screens/DarkPoolScreen").then(module => ({ default: module.DarkPoolScreen })));
const FlashCrashScreen = deferredScreen(() => import("@/screens/FlashCrashScreen").then(module => ({ default: module.FlashCrashScreen })));
const SymbolSearchScreen = deferredScreen(() => import("@/screens/SymbolSearchScreen"));
// A symbol verdict is composed from one canonical payload; it should not load until a reader opens a symbol.
const SymbolJudgmentScreen = deferredScreen(() => import("@/screens/SymbolJudgmentScreen"));
const InsightScreen = deferredScreen(() => import("@/screens/InsightScreen"));
const LearnScreen = deferredScreen(() => import("@/screens/LearnScreen"));
const TemporalArbitrageHub = deferredScreen(() => import("@/screens/TemporalArbitrageHub"));
const IvRvTermStructureScreen = deferredScreen(() => import("@/screens/IvRvTermStructureScreen").then(module => ({ default: module.IvRvTermStructureScreen })));
const AlphaAttributionScreen = deferredScreen(() => import("@/screens/AlphaAttributionScreen").then(module => ({ default: module.AlphaAttributionScreen })));
const CaseStudiesScreen = deferredScreen(() => import("@/screens/CaseStudiesScreen").then(module => ({ default: module.CaseStudiesScreen })));
const OcaTraceScreen = deferredScreen(() => import("@/screens/OcaTraceScreen").then(module => ({ default: module.OcaTraceScreen })));
const WatchlistScreenV2 = deferredScreen(() => import("@/screens/WatchlistScreenV2"));
const WatchSetupScreen = deferredScreen(() => import("@/screens/WatchSetupScreen"));
const ForecastScreenV2 = deferredScreen(() => import("@/screens/ForecastScreenV2"));
const AlertsScreenV2 = deferredScreen(() => import("@/screens/AlertsScreenV2"));
const CapitalFlowScreenV2 = deferredScreen(() => import("@/screens/CapitalFlowScreenV2"));
const ContagionMapScreenV2 = deferredScreen(() => import("@/screens/ContagionMapScreenV2"));
const EngineExplorerScreen = deferredScreen(() => import("@/screens/EngineExplorerScreen"));
const RiskMonitorScreen = deferredScreen(() => import("@/screens/RiskMonitorScreen"));
const EngineDeepDiveScreen = deferredScreen(() => import("@/screens/EngineDeepDiveScreen"));
const MonitoringRulesScreen = deferredScreen(() => import("@/screens/MonitoringRulesScreen"));
const ExtensionConnectScreen = deferredScreen(() => import("@/screens/ExtensionConnectScreen"));
const AnalyticsScreen = deferredScreen(() => import("@/screens/AnalyticsScreen"));
const DeepDiveScreen = deferredScreen(() => import("@/screens/DeepDiveScreen"));
const SentimentTimelineScreen = deferredScreen(() => import("@/screens/SentimentTimelineScreen"));
const VolatilitySurfaceScreen = deferredScreen(() => import("@/screens/InstrumentVolScreen"));
import { PROTOTYPE_SCREENS_LIVE } from "@/screens/V7/prototypeScreens";
const SituationWatchScreen = deferredScreen(() => import("@/screens/V7/SituationWatchScreen"));
const PortfolioYourMoneyScreen = deferredScreen(() => import("@/screens/V7/PortfolioYourMoneyScreen"));
const AttentionRadarScreen = deferredScreen(() => import("@/screens/V7/AttentionRadarScreen"));
const EvidenceGraphScreen = deferredScreen(() => import("@/screens/V7/EvidenceGraphScreen"));
const SymbolIntelScreen = deferredScreen(() => import("@/screens/V7/SymbolIntelScreen"));

import { TERMS_VERSION } from "@shared/termsVersion";
import { fetchWithAuth, getApiUrl } from "@/lib/query-client";

/**
 * Record Terms consent against the ACCOUNT, once a session exists.
 *
 * Lives here rather than on the sign-in buttons because Google sign-in resolves
 * through the auth listener and never calls signIn() directly — recording it
 * per-button would have silently missed that path, and a consent record with a
 * hole in it is worse than none, because it looks complete.
 *
 * The server is idempotent (first acceptance wins, COALESCE), so re-firing on
 * later sessions cannot overwrite when the person actually agreed.
 *
 * Deliberately fire-and-forget: this must never block the reader from entering
 * the product. If it fails, the server simply holds no record — which is the
 * truth, and is reported as such by GET /api/me/terms rather than assumed.
 */
function recordTermsConsent(): void {
  try {
    const base = getApiUrl() || "";
    void fetchWithAuth(`${base}/api/me/terms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version: TERMS_VERSION }),
    }).catch(() => {});
  } catch {
    /* never block entry on a consent write */
  }
}

export default function RootStackNavigator() {
  const screenOptions = useScreenOptions();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(true);
  const [hasOnboarded, setHasOnboarded] = useState(false);
  const pendingWatch = hasPendingWatchIntent();

  useEffect(() => {
    checkStoredState();
  }, []);

  // Consent is asked at COMMITMENT: the sign-up screen states it, and the
  // record is written the moment an authenticated session exists.
  useEffect(() => {
    if (user && !user.isDemo) recordTermsConsent();
  }, [user]);

  const checkStoredState = async () => {
    try {
      const [onboarding, legacyOnboarding] = await Promise.all([
        AsyncStorage.getItem(ONBOARDING_STORAGE_KEY),
        AsyncStorage.getItem(LEGACY_ONBOARDING_STORAGE_KEY),
      ]);
      setHasOnboarded(hasCompletedOnboarding(onboarding, legacyOnboarding));
    } catch {
      setHasOnboarded(false);
    } finally {
      setIsLoadingOnboarding(false);
    }
  };

  if (isLoadingOnboarding && user && !user.isDemo) {
    // Only an identified account can need onboarding. Anonymous readers mount
    // Today immediately while background identity restoration continues.
    return (
      <View style={{ flex: 1, backgroundColor: theme.backgroundRoot, alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Text style={{ color: theme.textSecondary, fontSize: 15, letterSpacing: 0.2 }}>Preparing your market read…</Text>
      </View>
    );
  }

  // OAuth authorization request (?client_id=…&redirect_uri=…): always show the
  // Auth screen so the consent flow runs regardless of login/terms/onboarding
  // state. A logged-in user still needs to explicitly approve the connection;
  // a logged-out user signs in there first, then consents.
  if (isOAuthFlow()) {
    return (
      <Stack.Navigator screenOptions={screenOptions}>
        <Stack.Screen
          name="Auth"
          component={AuthScreen}
          options={{ headerShown: false, animation: "fade" }}
        />
      </Stack.Navigator>
    );
  }

  /**
   * 🔴 NATIVE HANDOFF: the browser's only job here is to hand the session back
   * to the app that opened it, and that has to happen whether or not the user
   * is already signed in.
   *
   * This is the third in-flight protocol on this screen with the same shape as
   * the two beside it, and it was the one that was missing. `AuthScreen` owns
   * the mint (`completeNativeHandoff`, its only caller anywhere), but the gate
   * further down mounts `AuthScreen` only for `(!user || user.isDemo)`. Coming
   * back from Google the session is restored FIRST, so a real user exists, the
   * auth screen is skipped, and the dashboard renders — in the browser. The
   * app never receives anything and reports the tab as dismissed.
   *
   * Measured 2026-08-31 on the founder's handset:
   *   web  auth.google_redirect_dispatched
   *   web  floor.first_render {connected:true}   ← no auth.screen_viewed between
   *   android auth.web_handoff_cancelled how=dismiss
   * with `auth.native_handoff_minted` at **0 for its entire lifetime** — the
   * hand-back had never once run.
   *
   * Placed after the two branches above because it cannot co-occur with
   * either: an OAuth consent request and an extension connect both arrive on
   * their own query strings, from a different entry point than `?native=1`.
   */
  if (isNativeHandoffFlow()) {
    return (
      <Stack.Navigator screenOptions={screenOptions}>
        <Stack.Screen
          name="Auth"
          component={AuthScreen}
          options={{ headerShown: false, animation: "fade" }}
        />
      </Stack.Navigator>
    );
  }

  if (isExtensionConnectFlow()) {
    return (
      <Stack.Navigator screenOptions={screenOptions}>
        <Stack.Screen
          name="ExtensionConnect"
          component={ExtensionConnectScreen}
          options={{ headerShown: false, animation: "fade" }}
        />
      </Stack.Navigator>
    );
  }

  return (
    <Stack.Navigator screenOptions={screenOptions}>
      {/* 🔴 THE TERMS GATE WAS HERE, and was removed 2026-08-10 (founder decision).
          Consent is asked at COMMITMENT — account creation — not on arrival. A
          reader who has created nothing has nothing to consent about, and a wall
          before any value is the opposite of what this product sells.

          It did NOT simply move: it used to be client AsyncStorage, which was
          per-device, client-controlled and unauditable. Acceptance is now
          recorded server-side against the account with the document version, by
          POST /api/me/terms at sign-up. Consent you cannot prove is consent you
          do not have, so removing the screen without moving the RECORD would
          have left us worse off than the gate.

          Do not reintroduce a pre-auth Terms screen. If the Terms change, bump
          TERMS_VERSION in shared/termsVersion.ts and ask again at the next
          commitment point. */}
      {/* THE APP IS OPEN (founder decision, 2026-08-12). A visitor and a
          signed-in user reach the SAME app — no login wall, no pre-auth terms
          gate, no reduced public stand-in. `Main` is the destination for
          everyone; signing in adds a book and continuity, it does not unlock
          the product.

          The old `!user` branch sent visitors to a cut-down PublicRead screen
          instead of the app; that screen is deleted, not orphaned. Onboarding
          stays behind `user` because it configures an account a visitor does
          not have yet. A visitor carrying a landing-page watch intent still
          lands on WatchSetup (registered first below), so that continuity
          flow is unchanged. */}
      {user && !user.isDemo && shouldShowOnboarding(hasOnboarded, pendingWatch) ? (
        <Stack.Screen name="Onboarding" options={{ headerShown: false }}>
          {(props) => <OnboardingScreen {...props} onComplete={() => setHasOnboarded(true)} />}
        </Stack.Screen>
      ) : (
        <>
          {pendingWatch && (
            <Stack.Screen
              name="WatchSetup"
              component={WatchSetupScreen}
              options={{ headerShown: false, animation: "fade" }}
            />
          )}
          <Stack.Screen
            name="Main"
            component={MainTabNavigator}
            // The title is what a stack header's Back button reads ("Home, back"),
            // never the internal route name "Main".
            options={{ headerShown: false, title: "Home" }}
          />
          <Stack.Screen
            name="AlertDetail"
            component={AlertDetailScreen}
            options={({ navigation }) => ({
              presentation: "modal",
              headerTitle: "Alert Details",
              headerRight: () => (
                <Pressable
                  onPress={() => navigation.canGoBack() && navigation.goBack()}
                  style={{ paddingHorizontal: 8, paddingVertical: 4 }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Feather name="x" size={22} color={theme.textPrimary} />
                </Pressable>
              ),
            })}
          />
          <Stack.Screen
            name="Pricing"
            component={PricingScreen}
            options={{
              presentation: "modal",
              headerTitle: "Plans",
            }}
          />
          <Stack.Screen
            name="DarkPool"
            component={DarkPoolScreen}
            options={{ headerShown: false, animation: "fade" }}
          />
          <Stack.Screen
            name="FlashCrash"
            component={FlashCrashScreen}
            options={{ headerShown: false, animation: "fade" }}
          />
           <Stack.Screen
            name="SymbolSearch"
            component={SymbolSearchScreen}
            options={{ headerShown: false, animation: "fade_from_bottom" }}
          />
          <Stack.Screen
            name="Symbol"
            component={SymbolJudgmentScreen}
            options={{ headerShown: false, animation: "slide_from_right" }}
          />
          {/* "VerdictDetail" is DELETED (2026-08-12), not deprecated. It had one
              inbound route against 14 for "Symbol", so it was an orphan holding a
              full instrument redesign users could not reach; after its beats were
              hosted in SymbolScreen the two screens published contradictory
              verdicts for the same symbol because they read different endpoints.
              Its single caller (WireScreen → YourBookBlock onOpenVerdict) now
              navigates to "Symbol". Do not re-add a second instrument screen.

              🔴 "IntelligenceDetail" is DELETED TOO (2026-08-15) — and it was
              registered on the line directly BELOW that sentence for three days,
              which is how a rule written down fails to bind. It was the inverse
              of the VerdictDetail case and therefore worse: not an orphan
              nobody reached, but the OLD screen that seven live call sites
              still pointed at, so the redesign was the unreachable one. The
              founder tapped a card in the Floor's Action Center and landed on a
              raw engine dump — per-engine confidence percentages, "Direction —
              NOT REPORTED", "Primary Regime UNKNOWN", "HMM Score N/A" — the
              exact surface SymbolJudgmentScreen exists to replace.

              Deleting the REGISTRATION is what closes this, not repointing the
              callers: a caller can be re-added, a screen that is not mounted
              cannot be navigated to. `screens/IntelligenceDetailScreen.tsx` is
              deleted with it, because a file left in the tree is how the next
              agent re-registers it. Two instrument screens have now been
              deleted for the same reason. There is ONE: "Symbol". */}

          <Stack.Screen
            name="MarketHeatmap"
            component={MarketHeatmapScreen}
            options={{ headerShown: false, animation: "fade" }}
          />
          <Stack.Screen
            name="Insight"
            component={InsightScreen}
            options={({ navigation }) => ({
              headerTitle: "Market answers",
              headerStyle: { backgroundColor: "transparent" },
              headerTransparent: true,
              headerTintColor: "#A97826",
              headerLeft: () => (
                <Pressable
                  onPress={() => navigation.canGoBack() && navigation.goBack()}
                  accessibilityRole="button"
                  accessibilityLabel="Go back"
                  style={{ paddingHorizontal: 8, paddingVertical: 4 }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Feather name="arrow-left" size={22} color="#A97826" />
                </Pressable>
              ),
            })}
          />
          <Stack.Screen
            name="Accounts"
            component={AccountsScreen}
            options={({ navigation }) => ({
              headerTitle: "Connected Accounts",
              headerStyle: { backgroundColor: "transparent" },
              headerTransparent: true,
              headerTintColor: theme.textPrimary,
              headerLeft: () => (
                <Pressable
                  onPress={() => navigation.canGoBack() && navigation.goBack()}
                  accessibilityRole="button"
                  accessibilityLabel="Go back"
                  style={{ paddingHorizontal: 8, paddingVertical: 4 }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Feather name="arrow-left" size={22} color={theme.textPrimary} />
                </Pressable>
              ),
            })}
          />
          <Stack.Screen
            name="Learn"
            component={LearnScreen}
            options={{ headerTitle: "Learn", headerShown: false }}
          />
          {/* <Stack.Screen
            name="SignalMetadataDemo"
            component={SignalMetadataDemoScreen}
            options={{ headerShown: false }}
          /> */}
          <Stack.Screen
            name="ContagionMap"
            component={ContagionMapScreenV2}
            options={{ headerShown: false, animation: "fade" }}
          />
          <Stack.Screen
            name="CapitalFlow"
            component={CapitalFlowScreenV2}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="WatchlistV2"
            component={WatchlistScreenV2}
            options={{ headerShown: false }}
          />
          {!pendingWatch && (
            <Stack.Screen
              name="WatchSetup"
              component={WatchSetupScreen}
              options={{ headerShown: false, animation: "fade" }}
            />
          )}
          <Stack.Screen
            name="AlertsV2"
            component={AlertsScreenV2}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="ForecastPanel"
            component={ForecastScreenV2}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="TemporalArbitrage"
            component={TemporalArbitrageHub}
            options={{ headerShown: false, animation: "fade" }}
          />
          <Stack.Screen
            name="IvRvTermStructure"
            component={IvRvTermStructureScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="AlphaAttribution"
            component={AlphaAttributionScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="CaseStudies"
            component={CaseStudiesScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="OcaTrace"
            component={OcaTraceScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="EngineExplorer"
            component={EngineExplorerScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="RiskMonitor"
            component={RiskMonitorScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="EngineDeepDive"
            component={EngineDeepDiveScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="SentimentTimeline"
            component={SentimentTimelineScreen}
            // These three drew no Back of their own (walked 2026-10-01), so they
            // take the stack header and its Back instead of trapping the reader.
            options={{ title: "Sentiment" }}
          />
          <Stack.Screen
            name="VolatilitySurface"
            component={VolatilitySurfaceScreen}
            options={{ headerShown: false, animation: "fade" }}
          />
          <Stack.Screen
            name="Analytics"
            component={AnalyticsScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="MonitoringRules"
            component={MonitoringRulesScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="Developer"
            component={DeveloperScreen}
            options={{ title: "Developer API" }}
          />
          <Stack.Screen
            name="ConnectHub"
            component={ConnectHubScreen}
            options={{ title: "Connect Your AI" }}
          />
          <Stack.Screen
            name="ExtensionConnect"
            component={ExtensionConnectScreen}
            options={{ title: "Connect extension" }}
          />
          <Stack.Screen
            name="PortfolioCommand"
            component={PortfolioCommandScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="DeepDive"
            component={DeepDiveScreen}
            options={{ title: "Deep dive" }}
          />
          {/* Stitch prototypes: hardcoded figures, see screens/V7/prototypeScreens.ts */}
          {PROTOTYPE_SCREENS_LIVE && (
            <>
          <Stack.Screen
            name="SituationWatch"
            component={SituationWatchScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="PortfolioYourMoney"
            component={PortfolioYourMoneyScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="AttentionRadar"
            component={AttentionRadarScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="EvidenceGraph"
            component={EvidenceGraphScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="SymbolIntel"
            component={SymbolIntelScreen}
            options={{ headerShown: false }}
          />
            </>
          )}
        </>
      )}
      {/* Auth is reachable from inside the app while signed out, then removed
          from the route set as soon as authentication resolves. Keeping it
          registered for an authenticated user left successful sign-ins parked
          on this screen until reload because it remained the current route. */}
      {(!user || user.isDemo) && (
        <Stack.Screen
          name="Auth"
          component={AuthScreen}
          options={{ headerShown: false, animation: "fade" }}
        />
      )}
    </Stack.Navigator>
  );
}
